import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { Sr89DebugResults } from './Sr89DebugPage';

interface CompareFormData {
  calculationId1: string;
  calculationId2: string;
}

const Sr89ComparePage: React.FC = () => {
  const navigate = useNavigate();
  const [results1, setResults1] = useState<Sr89DebugResults | null>(null);
  const [results2, setResults2] = useState<Sr89DebugResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { control, handleSubmit, formState: { errors } } = useForm<CompareFormData>({
    defaultValues: {
      calculationId1: '',
      calculationId2: '',
    },
  });

  const onSubmit = async (data: CompareFormData) => {
    setLoading(true);
    setError(null);
    setResults1(null);
    setResults2(null);
    
    try {
      const token = localStorage.getItem("token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      
      const [response1, response2] = await Promise.all([
        axios.get(`http://localhost:8000/api/debug/sr89/${data.calculationId1}`, config),
        axios.get(`http://localhost:8000/api/debug/sr89/${data.calculationId2}`, config)
      ]);
      
      setResults1(response1.data);
      setResults2(response2.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al obtener los datos de depuración');
    } finally {
      setLoading(false);
    }
  };

  const calculateDifference = (val1: number, val2: number): string => {
    if (val2 === 0) return 'N/A';
    const diff = ((val1 - val2) / val2) * 100;
    return `${diff.toFixed(2)}%`;
  };

  const renderComparisonRow = (
    label: string,
    value1: number | undefined,
    value2: number | undefined,
    formula?: string
  ) => {
    if (value1 === undefined || value2 === undefined) return null;
    
    const difference = calculateDifference(value1, value2);
    const isDifferent = Math.abs(value1 - value2) > 0.000001;
    
    return (
      <TableRow sx={{ bgcolor: isDifferent ? 'warning.light' : 'inherit' }}>
        <TableCell>
          <Typography variant="body2" fontWeight="bold">
            {label}
          </Typography>
          {formula && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {formula}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontFamily="monospace">
            {value1.toExponential(6)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontFamily="monospace">
            {value2.toExponential(6)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography
            variant="body2"
            fontWeight="bold"
            color={isDifferent ? 'error' : 'success.main'}
          >
            {difference}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderSection = (title: string, rows: React.ReactNode[]) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Variable</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Cálculo 1
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Cálculo 2
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                Diferencia %
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            Comparación de Cálculos Sr-89
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/debug')}
          >
            Volver a Depuración
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" paragraph>
          Compara dos cálculos de Sr-89 lado a lado para identificar diferencias.
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Controller
                name="calculationId1"
                control={control}
                rules={{ required: 'El ID del primer cálculo es requerido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="ID Cálculo 1"
                    variant="outlined"
                    error={!!errors.calculationId1}
                    helperText={errors.calculationId1?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="calculationId2"
                control={control}
                rules={{ required: 'El ID del segundo cálculo es requerido' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="ID Cálculo 2"
                    variant="outlined"
                    error={!!errors.calculationId2}
                    helperText={errors.calculationId2?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Comparar'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {results1 && results2 && (
        <Box>
          {renderSection('1. Eficiencias', [
            renderComparisonRow('Eficiencia Sr-89', results1.efficiencySr89, results2.efficiencySr89),
            renderComparisonRow('Eficiencia Sr-90', results1.efficiencySr90, results2.efficiencySr90),
            renderComparisonRow('Eficiencia Y-90', results1.efficiencyY90, results2.efficiencyY90),
          ])}

          {renderSection('2. Constantes de Desintegración', [
            renderComparisonRow('λ Sr-89', results1.cteDisintegrationSr89, results2.cteDisintegrationSr89),
            renderComparisonRow('λ Sr-90', results1.cteDisintegrationSr90, results2.cteDisintegrationSr90),
            renderComparisonRow('λ Y-90', results1.cteDisintegrationY90, results2.cteDisintegrationY90),
          ])}

          {renderSection('3. Factores', [
            renderComparisonRow('Factor b', results1.factorB, results2.factorB, 'exp(-λ_y90*t2)'),
            renderComparisonRow('Factor c', results1.factorC, results2.factorC, 'exp(-λ_y90*t1)'),
            renderComparisonRow('Factor d', results1.factorD, results2.factorD, 'exp(-λ_sr89*t1)'),
            renderComparisonRow('Factor e', results1.factorE, results2.factorE, 'exp(-λ_sr89*t2)'),
            renderComparisonRow('Factor m', results1.factorM, results2.factorM),
            renderComparisonRow('Factor n', results1.factorN, results2.factorN),
          ])}

          {renderSection('4. CPM', [
            renderComparisonRow('CPM 1er Contaje', results1.cpm1Contaje, results2.cpm1Contaje),
            renderComparisonRow('CPM 2do Contaje', results1.cpm2Contaje, results2.cpm2Contaje),
            renderComparisonRow('CPM Fondo', results1.cpmFondo, results2.cpmFondo),
            renderComparisonRow('CPM Neto 1', results1.cpmNeto1, results2.cpmNeto1),
            renderComparisonRow('CPM Neto 2', results1.cpmNeto2, results2.cpmNeto2),
          ])}

          {renderSection('5. CPM₀ Sr-89', [
            renderComparisonRow('Numerador', results1.numeratorCpm0Sr89, results2.numeratorCpm0Sr89),
            renderComparisonRow('Denominador', results1.denominatorCpm0Sr89, results2.denominatorCpm0Sr89),
            renderComparisonRow('CPM₀ Sr-89', results1.cpm0Sr89, results2.cpm0Sr89),
            renderComparisonRow('Error CPM₀ Sr-89', results1.errorCpm0Sr89, results2.errorCpm0Sr89),
          ])}

          {renderSection('6. Cálculo de Actividad', [
            renderComparisonRow('Dividendo', results1.dividendoActivity, results2.dividendoActivity),
            renderComparisonRow('Divisor', results1.divisorActivity, results2.divisorActivity),
          ])}

          {renderSection('7. RESULTADOS FINALES', [
            renderComparisonRow('🎯 ACTIVIDAD (Bq/m³)', results1.activity, results2.activity),
            renderComparisonRow('📊 INCERTIDUMBRE', results1.uncertainty, results2.uncertainty),
            renderComparisonRow('🔍 LID', results1.lid, results2.lid),
          ])}
        </Box>
      )}
    </Box>
  );
};

export default Sr89ComparePage;
